import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface EnabledSource {
  slug: string;
  name: string;
  status: string;
  category: string;
  connection_status: string;
  external_username: string | null;
  last_sync_at: string | null;
}

type SourceItem = {
  name: string;
  type: string;
  status: string;
  lastSync: string | null;
  username: string | null;
};

function statusIndicator(status: string): { color: string; label: string } {
  switch (status) {
    case 'connected':
    case 'active':
    case 'enabled':
      return { color: 'var(--sp-ok)', label: 'Connected' };
    case 'pending':
    case 'pending_auth':
      return { color: 'var(--sp-warn)', label: 'Pending' };
    case 'skipped':
      return { color: 'var(--sp-text-dim)', label: 'Skipped' };
    case 'error':
    case 'disconnected':
    case 'expired':
      return { color: 'var(--sp-error)', label: 'Error' };
    case 'coming_soon':
      return { color: 'var(--sp-text-dim)', label: 'Coming soon' };
    default:
      return { color: 'var(--sp-text-dim)', label: status };
  }
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function SourceHealthWidget({ context }: WidgetProps) {
  const [sources, setSources] = useState<SourceItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void engineFetchJson<EnabledSource[]>('/sources/enabled')
      .then((enabled) => {
        const items: SourceItem[] = enabled.map((source) => ({
          name: source.name || source.slug,
          type: source.category ?? 'source',
          status: source.connection_status || source.status || 'enabled',
          lastSync: source.last_sync_at,
          username: source.external_username,
        }));
        setSources(items);
        setError(null);
      })
      .catch(() => {
        setError('Source status is not available in this engine build');
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Source Health</span>
        <a href="/sources" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Manage
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {error ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              GET /api/v1/sources/enabled
            </div>
            {/* Mock layout */}
            <div style={{ marginTop: 12, opacity: 0.4 }}>
              {['Twitter', 'Gmail', 'Google Drive'].map((name) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sp-text-dim)' }} />
                  <span style={{ color: 'var(--sp-text-muted)' }}>{name}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginLeft: 'auto' }}>--</span>
                </div>
              ))}
            </div>
          </div>
        ) : sources ? (
          sources.length === 0 ? (
            <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, padding: '8px 0' }}>
              No sources connected yet.{' '}
              <a href="/sources">Connect a source</a>
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              {sources.map((s, idx) => {
                const ind = statusIndicator(s.status);
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '4px 0',
                      fontSize: 12,
                      borderBottom: idx < sources.length - 1 ? '1px solid var(--sp-border)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: ind.color,
                        flexShrink: 0,
                      }}
                      title={ind.label}
                    />
                    <span style={{ flex: 1, color: 'var(--sp-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.name}
                    </span>
                    {s.username && (
                      <span style={{ color: 'var(--sp-text-dim)', fontSize: 10 }}>@{s.username}</span>
                    )}
                    <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                      {s.lastSync ? timeAgo(s.lastSync) : '--'}
                    </span>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading sources...</div>
        )}
      </div>
    </div>
  );
}

export const sourceHealthWidget: WidgetDefinition = {
  id: 'source-health',
  name: 'Source Health',
  description: 'Connected data sources status grid with sync times.',
  icon: 'database',
  category: 'sources',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: SourceHealthWidget,
};
